import type { EventAttendeeListVisibility } from "@/db/schema/event";
import type { Gender } from "@/db/schema/profile";

export type EventMembershipStatus = "active" | "pending" | null;

type EventAccessOptions = {
  hasSessionUser: boolean;
  membershipStatus: EventMembershipStatus;
  membershipTierId: number | null;
  allowedTierIds: number[];
  viewerGender: Gender | null;
  allowedGenders: Gender[];
  hasUpcomingOccurrences: boolean;
  hasBookableOccurrences?: boolean;
};

type EventAttendeeListAccessOptions = {
  visibility: EventAttendeeListVisibility;
  hasSessionUser: boolean;
  membershipStatus: EventMembershipStatus;
  membershipTierId: number | null;
  allowedTierIds: number[];
  isAttendee: boolean;
};

export const isRestrictedEvent = (allowedTierIds: number[]) =>
  allowedTierIds.length > 0;

export const isPublicEvent = (allowedTierIds: number[]) =>
  allowedTierIds.length === 0;

export const isGenderRestrictedEvent = (allowedGenders: Gender[]) =>
  allowedGenders.length > 0;

/**
 * An event only the holders of one pack may book.
 *
 * A separate question from tier access, and deliberately so: a tier is who you
 * ARE in a hiive, a pack is what you BOUGHT. Salsa Nova's monthly session is
 * for the dancers on this month's batch pass, and expressing that as a
 * membership tier would hand a four-week course a permanent identity it should
 * not have — and leave the studio pruning the tier every month.
 */
export const isPackExclusiveEvent = (packExclusivePackId: number | null) =>
  packExclusivePackId != null;

/**
 * Whether this viewer holds the pack the event is reserved for.
 *
 * `heldPackIds` is what the viewer holds RIGHT NOW and usably — an expired or
 * refunded pass is not a ticket to the room, and a frozen one is a pass whose
 * clock the member chose to stop.
 */
export const meetsEventPackRequirement = (
  packExclusivePackId: number | null,
  heldPackIds: number[],
) =>
  !isPackExclusiveEvent(packExclusivePackId) ||
  (packExclusivePackId != null && heldPackIds.includes(packExclusivePackId));

export const meetsEventTierRequirement = (
  membershipTierId: number | null,
  allowedTierIds: number[],
) =>
  isPublicEvent(allowedTierIds) ||
  (membershipTierId != null && allowedTierIds.includes(membershipTierId));

export const meetsEventGenderRequirement = (
  viewerGender: Gender | null,
  allowedGenders: Gender[],
) =>
  !isGenderRestrictedEvent(allowedGenders) ||
  (viewerGender != null && allowedGenders.includes(viewerGender));

/** The restriction for an event with nothing left on its calendar. */
export const NO_UPCOMING_DATES_RESTRICTION = "There are no upcoming dates.";

/**
 * Reasons nobody can book, regardless of who is asking: there are no upcoming
 * dates, or every date's booking window is shut.
 *
 * Split out from the viewer checks so a signed-out visitor can be told the
 * truth. The combined check used to test `hasSessionUser` first, so a guest
 * looking at an event whose bookings closed weeks ago was told "Sign in to book
 * this event." — they signed in and hit the real wall one step later.
 */
export function getEventAvailabilityRestriction({
  hasUpcomingOccurrences,
  hasBookableOccurrences,
}: Pick<
  EventAccessOptions,
  "hasUpcomingOccurrences" | "hasBookableOccurrences"
>) {
  if (!hasUpcomingOccurrences) {
    return NO_UPCOMING_DATES_RESTRICTION;
  }

  if (typeof hasBookableOccurrences === "boolean" && !hasBookableOccurrences) {
    return "Bookings for this event are currently closed.";
  }

  return undefined;
}

/**
 * What is actually standing between this viewer and a ticket, as a value rather
 * than a sentence.
 *
 * The sentence version (`getEventViewerRestriction`) has to collapse everything
 * into one string, which is fine for a footer caption and useless for deciding
 * where a button should go. A tier-restricted event needs the CTA to point at
 * the tier that unlocks it — including for a signed-out visitor, who is the
 * common case when the event was shared on social media. Ordering `sign_in`
 * first, as the string version does, hides the one fact that visitor needs.
 */
export type EventEligibilityGate =
  | { kind: "sign_in" }
  | { kind: "needs_membership" }
  | { kind: "request_pending" }
  | { kind: "wrong_tier" }
  | { kind: "needs_gender" }
  | { kind: "gender_excluded" }
  /** The event is reserved for holders of a pack this viewer does not hold. */
  | { kind: "needs_pack"; packName: string | null };

export function getEventEligibilityGate({
  hasSessionUser,
  membershipStatus,
  membershipTierId,
  allowedTierIds,
  viewerGender,
  allowedGenders,
  hasPendingTierRequest = false,
  packExclusivePackId = null,
  packExclusivePackName = null,
  heldPackIds = [],
}: Omit<
  EventAccessOptions,
  "hasUpcomingOccurrences" | "hasBookableOccurrences"
> & {
  /** The pack this event is reserved for, when it is reserved for one. */
  packExclusivePackId?: number | null;
  /** Its name, so the wall can say WHICH pack rather than "a pack". */
  packExclusivePackName?: string | null;
  /** Packs the viewer holds and could use today. */
  heldPackIds?: number[];
  /**
   * A join request awaiting a queen's review. Deliberately separate from
   * `membershipStatus`: a request lives in `hiive_tier_join_request` and does
   * NOT create a membership row, so without this the event page keeps saying
   * "Join hiive" to somebody who already did.
   */
  hasPendingTierRequest?: boolean;
}): EventEligibilityGate | undefined {
  const restricted = isRestrictedEvent(allowedTierIds);

  // A tier wall outranks the sign-in wall. Somebody who arrived from a shared
  // link needs to know the price of entry is a tier, not an account — telling
  // them to sign in first only moves the same refusal one screen along.
  if (
    restricted &&
    !meetsEventTierRequirement(membershipTierId, allowedTierIds)
  ) {
    if (hasPendingTierRequest) return { kind: "request_pending" };
    if (membershipStatus === "pending") return { kind: "request_pending" };
    if (membershipTierId != null && membershipStatus === "active") {
      return { kind: "wrong_tier" };
    }
    return { kind: "needs_membership" };
  }

  if (!hasSessionUser) return { kind: "sign_in" };

  if (restricted && membershipStatus !== "active") {
    return hasPendingTierRequest || membershipStatus === "pending"
      ? { kind: "request_pending" }
      : { kind: "needs_membership" };
  }

  // After the sign-in wall, because "buy the pass" is useless advice to
  // somebody with no account to hold it on — and before gender, because a pack
  // wall is the specific, actionable thing about THIS event.
  if (!meetsEventPackRequirement(packExclusivePackId, heldPackIds)) {
    return { kind: "needs_pack", packName: packExclusivePackName };
  }

  if (isGenderRestrictedEvent(allowedGenders) && !viewerGender) {
    return { kind: "needs_gender" };
  }

  if (!meetsEventGenderRequirement(viewerGender, allowedGenders)) {
    return { kind: "gender_excluded" };
  }

  return undefined;
}

/** Reasons *this* viewer cannot book: no session, or membership/tier/gender. */
export function getEventViewerRestriction({
  hasSessionUser,
  membershipStatus,
  membershipTierId,
  allowedTierIds,
  viewerGender,
  allowedGenders,
}: Omit<
  EventAccessOptions,
  "hasUpcomingOccurrences" | "hasBookableOccurrences"
>) {
  if (!hasSessionUser) {
    return "Sign in to book this event.";
  }

  const restricted = isRestrictedEvent(allowedTierIds);

  if (restricted && membershipStatus !== "active") {
    return membershipStatus === "pending"
      ? "Your hiive membership is pending approval."
      : "Join this hiive to book this event.";
  }

  if (!meetsEventTierRequirement(membershipTierId, allowedTierIds)) {
    return "This event is limited to specific hiive tiers.";
  }

  /*
   * A missing gender is deliberately NOT a restriction.
   *
   * It used to be, and the result was a dead end: the footer read "Add your
   * gender to your profile to book this event." with no button under it, so
   * the only way through was for the viewer to work out on their own that the
   * answer lived in profile settings. It is one enum with three options — the
   * checkout asks for it (`getEventDeferredProfileAnswers`) and writes it to
   * the profile, and `createEventBooking` still refuses anyone whose stored
   * gender does not match. See the deferred-answers block below.
   */
  if (!meetsEventGenderRequirement(viewerGender, allowedGenders)) {
    return isGenderRestrictedEvent(allowedGenders) && !viewerGender
      ? undefined
      : "This event is limited to selected genders.";
  }

  return undefined;
}

/**
 * Profile facts this event needs that checkout can collect on the spot, rather
 * than refusing the booking and sending the viewer off to find a settings page.
 *
 * Only ever facts that are (a) a closed set of options, so there is nothing to
 * moderate or verify, and (b) genuinely unknown — never a re-ask of something
 * already stored, which would turn checkout into an editing surface for the one
 * field that decides eligibility.
 *
 * The list is advice to the UI. Every answer is written through
 * `saveDeferredProfileAnswers` and then re-read from the database by
 * `createEventBooking`, so a client that skips the questions is refused, not
 * trusted.
 */
export type EventDeferredProfileAnswer = "gender";

/**
 * Plain-language names for the genders an event admits, for copy that has to
 * explain a refusal to the person it refused.
 *
 * `allowedGenders` holds profile enum values (`female`), and putting those on
 * screen produces "This event is limited to selected genders." — technically
 * true and completely unhelpful, because it never says which. Somebody who has
 * just answered a question and been turned away is owed the actual reason.
 */
const GENDER_AUDIENCE_LABEL: Record<Gender, string> = {
  male: "men",
  female: "women",
  // Matches the wording already on the event page ("Open to women &
  // non-binary people"). The enum value is `other`, but "other genders" reads
  // as an afterthought, and in a list it produced "women and non-binary and
  // other genders" — two `and`s for one audience.
  other: "non-binary people",
};

export function describeAllowedGenders(allowedGenders: Gender[]): string {
  // Ordered by the enum, not by however the queen happened to tick the boxes,
  // so the same audience always reads the same way.
  const labels = (["female", "male", "other"] as const)
    .filter((gender) => allowedGenders.includes(gender))
    .map((gender) => GENDER_AUDIENCE_LABEL[gender]);

  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * The sentence shown to somebody whose answer disqualifies them. `null` when
 * the answer is fine — the caller proceeds instead of rendering anything.
 */
export function getGenderExclusionMessage(
  viewerGender: Gender | null,
  allowedGenders: Gender[],
): string | null {
  if (meetsEventGenderRequirement(viewerGender, allowedGenders)) return null;
  const audience = describeAllowedGenders(allowedGenders);
  return audience
    ? `This event is open to ${audience} only, so you can't book a ticket for yourself. Your answer has been saved to your profile.`
    : "This event is limited to selected genders.";
}

export function getEventDeferredProfileAnswers({
  hasSessionUser,
  viewerGender,
  allowedGenders,
}: Pick<
  EventAccessOptions,
  "hasSessionUser" | "viewerGender" | "allowedGenders"
>): EventDeferredProfileAnswer[] {
  // Nothing to ask a visitor with no account: there is no profile row to write
  // to yet. They sign in first, and the question is waiting on the way back.
  if (!hasSessionUser) return [];
  if (isGenderRestrictedEvent(allowedGenders) && !viewerGender) {
    return ["gender"];
  }
  return [];
}

/**
 * Can a signed-out visitor start this booking, deferring sign-in to checkout?
 *
 * Yes when the only thing standing between them and a ticket is identity, and
 * signing in is guaranteed to resolve it. Tier-restricted events are the
 * exception: they need a membership, which no sign-in can conjure, so the
 * honest CTA there is still the tier — walking someone to the pass picker only
 * to refuse them at checkout is worse than asking up front.
 *
 * A gender-restricted event used to sit in that exception too, and it should
 * not have: the missing fact is one question with three answers, so the flow
 * can sign the visitor in and then ask it. Being refused for the answer you
 * gave is a fair outcome; being refused before anybody asked is just a wall.
 */
export function canStartGuestEventBooking({
  hasSessionUser,
  allowedTierIds,
  hasUpcomingOccurrences,
  hasBookableOccurrences,
}: Pick<
  EventAccessOptions,
  | "hasSessionUser"
  | "allowedTierIds"
  | "hasUpcomingOccurrences"
  | "hasBookableOccurrences"
>) {
  if (hasSessionUser) return false;
  if (isRestrictedEvent(allowedTierIds)) return false;
  return (
    getEventAvailabilityRestriction({
      hasUpcomingOccurrences,
      hasBookableOccurrences,
    }) === undefined
  );
}

export function canBookEventFromRestriction(
  hasSessionUser: boolean,
  bookingRestriction: string | undefined,
) {
  return hasSessionUser && !bookingRestriction;
}

export function getEventAttendeeListAccess({
  visibility,
  hasSessionUser,
  membershipStatus,
  membershipTierId,
  allowedTierIds,
  isAttendee,
}: EventAttendeeListAccessOptions) {
  switch (visibility) {
    case "public":
      return { canViewCount: true, canViewList: true };
    case "members": {
      const canView = hasSessionUser && membershipStatus === "active";
      return { canViewCount: canView, canViewList: canView };
    }
    case "tiers": {
      const canView =
        hasSessionUser &&
        membershipStatus === "active" &&
        membershipTierId != null &&
        allowedTierIds.includes(membershipTierId);
      return { canViewCount: canView, canViewList: canView };
    }
    case "attendees":
      return { canViewCount: isAttendee, canViewList: isAttendee };
    case "count_only":
      return { canViewCount: true, canViewList: false };
    default:
      return { canViewCount: false, canViewList: false };
  }
}
